import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { jsPDF } from "jspdf";
import "./PdfReview.css"; // Reuse styling

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const LOG_PREFIX = "[PdfFeedbackViewer]";

/**
 * PdfFeedbackViewer - Display customer PDF feedback with drawings and comments
 * Props:
 *   - pdfUrl: URL to PDF file
 *   - feedbackData: Array of page feedback objects { page_number, comment, drawing }
 *   - initialPage: Which page to start on (default 1)
 *   - onClose: Callback when closing
 *   - isFullPage: Boolean - if true, render as full-page (not modal)
 */
function PdfFeedbackViewer({ pdfUrl, feedbackData = [], initialPage = 1, onClose, isFullPage = false }) {
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadFormat, setDownloadFormat] = useState(null); // null | "current" | "all"

  const pdfCanvasRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const pdfDocRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const renderTokenRef = useRef(0);
  const charCanvasRef = useRef(null); // For canvas downloads

  const debugLog = useCallback((...args) => {
    console.log(LOG_PREFIX, ...args);
  }, []);

  const feedbackPages = useMemo(() => {
    return [...new Set(
      feedbackData
        .map((f) => Number(f.page_number))
        .filter((p) => Number.isFinite(p) && p > 0)
    )].sort((a, b) => a - b);
  }, [feedbackData]);

  const feedbackPageIndex = feedbackPages.indexOf(currentPage);
  const prevFeedbackPage = feedbackPageIndex > 0 ? feedbackPages[feedbackPageIndex - 1] : null;
  const nextFeedbackPage = feedbackPageIndex >= 0 && feedbackPageIndex < feedbackPages.length - 1
    ? feedbackPages[feedbackPageIndex + 1]
    : null;

  // Load PDF document
  useEffect(() => {
    if (!pdfUrl) {
      debugLog("No PDF URL provided", { pdfUrl });
      setError("PDF URL not available");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        debugLog("Loading PDF", { pdfUrl });
        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
        pdfDocRef.current = pdf;
        setPageCount(pdf.numPages);
        const hasFeedbackPages = feedbackPages.length > 0;
        const fallbackPage = Math.min(initialPage, pdf.numPages);
        const fullPageStart = hasFeedbackPages
          ? (feedbackPages.includes(fallbackPage) ? fallbackPage : feedbackPages[0])
          : fallbackPage;
        setCurrentPage(isFullPage ? fullPageStart : fallbackPage);
        debugLog("PDF loaded", { numPages: pdf.numPages });
        setLoading(false);
      } catch (err) {
        debugLog("PDF load error", err);
        setError(`Failed to load PDF: ${err.message}`);
        setLoading(false);
      }
    })();
  }, [pdfUrl, debugLog, initialPage, feedbackPages, isFullPage]);

  // Render page (PDF layer) - matching PdfReview.js scaling for coordinate alignment
  const renderPage = useCallback(async (pageNum) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = pdfCanvasRef.current;
    const container = containerRef.current;
    if (!pdfDoc || !canvas || !container) return;

    debugLog("Render PDF page", { pageNum });

    // Cancel previous render
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch (e) { /* ignore */ }
      renderTaskRef.current = null;
    }

    const token = ++renderTokenRef.current;
    try {
      const page = await pdfDoc.getPage(pageNum);
      
      // Always use a fixed 900px reference width for the canvas pixel buffer.
      // PdfReview.js stores stroke coordinates at Math.min(containerWidth-32, 900)
      // which is 900 on desktop. By using the same pixel buffer width, stroke
      // coordinates align exactly. CSS handles visual scaling to fit the container.
      const REFERENCE_WIDTH = 900;
      const viewport = page.getViewport({ scale: 1 });
      const scale = REFERENCE_WIDTH / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const drawCanvas = drawCanvasRef.current;
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      if (drawCanvas) {
        drawCanvas.width = scaledViewport.width;
        drawCanvas.height = scaledViewport.height;
      }

      // CSS-scale canvases to fit the visible container without changing pixel buffer
      const displayWidth = Math.min(container.clientWidth - 32, REFERENCE_WIDTH);
      const displayScale = displayWidth / scaledViewport.width;
      const cssW = `${Math.round(scaledViewport.width * displayScale)}px`;
      const cssH = `${Math.round(scaledViewport.height * displayScale)}px`;
      canvas.style.width = cssW;
      canvas.style.height = cssH;
      if (drawCanvas) {
        drawCanvas.style.width = cssW;
        drawCanvas.style.height = cssH;
      }

      const ctx = canvas.getContext("2d");
      const renderTask = page.render({
        canvasContext: ctx,
        viewport: scaledViewport,
        annotationMode: pdfjsLib.AnnotationMode.DISABLE,
        renderInteractiveForms: false,
      });
      renderTaskRef.current = renderTask;

      await renderTask.promise;

      // Only continue if this render is still current
      if (token === renderTokenRef.current) {
        debugLog("PDF page rendered", { pageNum });
        replayFeedback(pageNum);
      }
    } catch (err) {
      if (token === renderTokenRef.current) {
        debugLog("Render error", err);
      }
    }
  }, []);

  // Get feedback for a specific page
  const getPageFeedback = useCallback((pageNum) => {
    const feedback = feedbackData.find((f) => f.page_number === pageNum) || null;
    if (!feedback) return null;
    
    // Handle both API response formats and string/object drawing_data
    let drawing = [];
    if (feedback.drawing) {
      drawing = Array.isArray(feedback.drawing) ? feedback.drawing : [];
    } else if (feedback.drawing_data) {
      try {
        if (typeof feedback.drawing_data === 'string') {
          drawing = JSON.parse(feedback.drawing_data) || [];
        } else if (Array.isArray(feedback.drawing_data)) {
          drawing = feedback.drawing_data;
        }
      } catch (e) {
        drawing = [];
      }
    }
    
    return {
      page_number: feedback.page_number,
      comment: feedback.comment || feedback.comment_text || "",
      drawing: drawing
    };
  }, [feedbackData]);

  // Draw strokes exactly as PdfReview.js does — raw coordinates, no scaling.
  // The canvas pixel buffer is always 900px-based, matching where strokes were captured.
  const drawStrokesOnContext = useCallback((ctx, strokes) => {
    if (!Array.isArray(strokes)) return;
    for (const stroke of strokes) {
      if (!stroke || !stroke.color || !Array.isArray(stroke.points) || stroke.points.length === 0) continue;
      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.size || 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (let i = 0; i < stroke.points.length; i++) {
        const p = stroke.points[i];
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    }
  }, []);

  // Replay customer drawings on overlay canvas
  const replayFeedback = useCallback((pageNum) => {
    const drawCanvas = drawCanvasRef.current;
    if (!drawCanvas) return;

    const feedback = getPageFeedback(pageNum);
    if (!feedback || !feedback.drawing) {
      const ctx = drawCanvas.getContext("2d");
      ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
      return;
    }

    const ctx = drawCanvas.getContext("2d");
    ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);

    const strokes = feedback.drawing;
    drawStrokesOnContext(ctx, strokes);
    let count = 0;
    for (const stroke of strokes) {
      if (stroke && Array.isArray(stroke.points) && stroke.points.length > 0) count += 1;
    }
    debugLog("Feedback replayed", { pageNum, strokes: count });
  }, [feedbackData, getPageFeedback, drawStrokesOnContext]);

  // Navigate pages
  useEffect(() => {
    if (pdfDocRef.current && pageCount > 0) {
      renderPage(currentPage);
    }
  }, [currentPage, pageCount, renderPage]);

  const addPageToPdf = async (pdf, pageNum, addNewPage) => {
    const pdfDoc = pdfDocRef.current;
    if (!pdfDoc) return;

    const feedback = getPageFeedback(pageNum);
    const page = await pdfDoc.getPage(pageNum);
    const baseViewport = page.getViewport({ scale: 1 });
    // Use same 900px reference width as the canvas pixel buffer and PdfReview.js
    const REFERENCE_WIDTH = 900;
    const scale = REFERENCE_WIDTH / baseViewport.width;
    const viewport = page.getViewport({ scale });

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = viewport.width;
    tempCanvas.height = viewport.height;
    const tempCtx = tempCanvas.getContext("2d");

    const renderTask = page.render({
      canvasContext: tempCtx,
      viewport,
      annotationMode: pdfjsLib.AnnotationMode.DISABLE,
      renderInteractiveForms: false,
    });
    await renderTask.promise;

    if (feedback && feedback.drawing) {
      drawStrokesOnContext(tempCtx, feedback.drawing);
    }

    const imgData = tempCanvas.toDataURL("image/png");
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    const footerHeight = 10;
    const feedbackBlockHeight = 34;

    if (addNewPage) {
      pdf.addPage([pageWidth, pageHeight]);
    }

    const availableImageWidth = pageWidth - margin * 2;
    const availableImageHeight = pageHeight - margin * 2 - feedbackBlockHeight - footerHeight;
    const imageScale = Math.min(availableImageWidth / tempCanvas.width, availableImageHeight / tempCanvas.height);
    const imageWidth = tempCanvas.width * imageScale;
    const imageHeight = tempCanvas.height * imageScale;
    const imageX = (pageWidth - imageWidth) / 2;
    const imageY = margin;

    pdf.addImage(imgData, "PNG", imageX, imageY, imageWidth, imageHeight, undefined, "FAST");

    const commentY = imageY + imageHeight + 6;
    const commentText = feedback?.comment?.trim() || "No feedback message on this page.";
    const wrapped = pdf.splitTextToSize(commentText, pageWidth - margin * 2 - 4);

    pdf.setDrawColor(210, 218, 255);
    pdf.setFillColor(245, 247, 255);
    pdf.roundedRect(margin, commentY, pageWidth - margin * 2, 24, 2, 2, "FD");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(55, 65, 81);
    pdf.text("User Feedback Message:", margin + 3, commentY + 5);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(31, 41, 55);
    pdf.text(wrapped.slice(0, 3), margin + 3, commentY + 10);

    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8);
    pdf.setTextColor(102, 126, 234);
    pdf.text(`\u00A9 ${new Date().getFullYear()} Powered by Photentra AI `, pageWidth / 2, pageHeight - 4, { align: "center" });
  };

  // Download currently visible page as PDF
  const downloadCurrentPagePdf = async () => {
    try {
      if (!getPageFeedback(currentPage)) {
        setError("This page has no feedback to export");
        return;
      }
      setDownloadFormat("current");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      await addPageToPdf(pdf, currentPage, false);
      pdf.save(`feedback-page-${currentPage}.pdf`);
      debugLog("Downloaded current page PDF", { page: currentPage });
    } catch (err) {
      debugLog("Current page PDF download error", err);
    } finally {
      setDownloadFormat(null);
    }
  };

  // Download only reviewed/feedback pages as a single PDF
  const downloadFeedbackPagesPdf = async () => {
    debugLog("Preparing feedback pages PDF download...");
    setDownloadFormat("all");

    try {
      if (feedbackPages.length === 0) {
        setError("No feedback pages available to download");
        setDownloadFormat(null);
        return;
      }

      const sortedPages = feedbackPages;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      for (let i = 0; i < sortedPages.length; i++) {
        await addPageToPdf(pdf, sortedPages[i], i > 0);
      }

      pdf.save("feedback-pages.pdf");
      debugLog("Downloaded feedback pages PDF", { pages: sortedPages });
      setDownloadFormat(null);
    } catch (err) {
      debugLog("Download error", err);
      setDownloadFormat(null);
    }
  };

  if (loading) {
    return (
      <div className="pr-modal-overlay">
        <div className="pr-modal">
          <div style={{ padding: "20px", textAlign: "center" }}>Loading feedback...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pr-modal-overlay" onClick={onClose}>
        <div className="pr-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: "20px", color: "red" }}>{error}</div>
          <button className="pr-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const feedback = getPageFeedback(currentPage);

  // Render modal version
  if (!isFullPage) {
    return (
      <div className="pr-modal-overlay" onClick={onClose}>
        <div className="pr-modal pr-feedback-modal" onClick={(e) => e.stopPropagation()}>
          <div className="pr-modal-header">
            <h3>Customer Feedback - Page {currentPage} of {pageCount}</h3>
            <button className="pr-btn-close" onClick={onClose}>✕</button>
          </div>

          <div className="pr-feedback-container" ref={containerRef}>
            {/* Canvas area */}
            <div className="pr-canvas-wrapper">
              <canvas ref={pdfCanvasRef} className="pr-pdf-canvas" />
              <canvas ref={drawCanvasRef} className="pr-draw-canvas" />
            </div>

            {/* Right sidebar - Comments and controls */}
            <div className="pr-feedback-sidebar">
              {/* Page comments */}
              <div className="pr-comments-section">
                <h4>Comments on this page:</h4>
                {feedback?.comment ? (
                  <div className="pr-comment-box">
                    <p>{feedback.comment}</p>
                  </div>
                ) : (
                  <p className="pr-no-comment">No comments on this page</p>
                )}
              </div>

              {/* Download buttons */}
              <div className="pr-download-section">
                <h4>Download:</h4>
                <button
                  className="pr-btn-secondary"
                  onClick={downloadCurrentPagePdf}
                  disabled={downloadFormat === "all" || !feedback}
                >
                  PDF: This Page
                </button>
                <button
                  className="pr-btn-secondary"
                  onClick={downloadFeedbackPagesPdf}
                  disabled={downloadFormat !== null}
                >
                  {downloadFormat === "all" ? "Downloading..." : "PDF: Feedback Pages"}
                </button>
              </div>

              {/* Feedback summary */}
              <div className="pr-feedback-summary">
                <h4>Summary:</h4>
                <p>{feedbackData.length} pages with feedback</p>
                {feedbackData.map((f) => {
                  const normalized = getPageFeedback(f.page_number);
                  return (
                    <div
                      key={f.page_number}
                      className={`pr-page-indicator ${currentPage === f.page_number ? "active" : ""}`}
                      onClick={() => setCurrentPage(f.page_number)}
                    >
                      Page {f.page_number}
                      {normalized?.drawing && normalized.drawing.length > 0 && ` (${normalized.drawing.length} strokes)`}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="pr-nav-controls">
            <button
              className="pr-btn-nav"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <span className="pr-page-info">
              Page {currentPage} / {pageCount}
            </span>
            <button
              className="pr-btn-nav"
              onClick={() => setCurrentPage(Math.min(pageCount, currentPage + 1))}
              disabled={currentPage === pageCount}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render full-page version
  return (
    <div className="pr-fullpage-wrapper">
      <div className="pr-fullpage-header">
        <div className="pr-fullpage-header-left">
          <span className="pr-fullpage-header-icon">📋</span>
          <div>
            <h2>Customer Feedback</h2>
            <span className="pr-fullpage-header-sub">
              {feedbackPageIndex >= 0 ? `Page ${currentPage} · Feedback ${feedbackPageIndex + 1} of ${feedbackPages.length}` : `Page ${currentPage}`}
            </span>
          </div>
        </div>
        <div className="pr-fullpage-header-actions">
          <button className="pr-header-btn" onClick={downloadCurrentPagePdf} disabled={downloadFormat !== null || !feedback}>
            ⬇ This Page
          </button>
          <button className="pr-header-btn" onClick={downloadFeedbackPagesPdf} disabled={downloadFormat !== null}>
            {downloadFormat === "all" ? "Downloading..." : "⬇ All Feedback"}
          </button>
        </div>
      </div>

      <div className="pr-fullpage-container">
        <div ref={containerRef} className="pr-fullpage-canvas-area">
          <div className="pr-canvas-wrapper pr-feedback-page-frame">
            <canvas ref={pdfCanvasRef} className="pr-pdf-canvas" />
            <canvas ref={drawCanvasRef} className="pr-draw-canvas pr-feedback-readonly" />
          </div>
        </div>

        <div className="pr-fullpage-feedback-panel">
          <h4>User Feedback Message</h4>
          {feedback?.comment ? (
            <div className="pr-comment-box pr-comment-box-light">
              <p>{feedback.comment}</p>
            </div>
          ) : (
            <p className="pr-no-comment">No message on this page.</p>
          )}
          <div className="pr-fullpage-meta">
            {feedbackPageIndex >= 0 ? `Feedback ${feedbackPageIndex + 1} of ${feedbackPages.length}` : `Page ${currentPage}`}
          </div>
        </div>
      </div>

      <div className="pr-fullpage-footer">
        <button className="pr-btn-nav" onClick={() => prevFeedbackPage && setCurrentPage(prevFeedbackPage)} disabled={!prevFeedbackPage}>
          ← Previous
        </button>
        <span className="pr-page-info">
          {feedbackPageIndex >= 0 ? `${feedbackPageIndex + 1} / ${feedbackPages.length}` : `Page ${currentPage}`}
        </span>
        <button className="pr-btn-nav" onClick={() => nextFeedbackPage && setCurrentPage(nextFeedbackPage)} disabled={!nextFeedbackPage}>
          Next →
        </button>
      </div>
    </div>
  );
}

export default PdfFeedbackViewer;
